import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScheduleBlock, WeeklyScheduleResponse } from '@crm/shared';
import { extractApiError } from '../../lib/api';

type Block = ScheduleBlock & { tempId: string };

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const; // pn-niedz (UI po polsku)

let tempCounter = 0;
function makeTempId(): string {
  tempCounter += 1;
  return `tmp_${Date.now()}_${tempCounter}`;
}

function blocksFromResponse(resp: WeeklyScheduleResponse | undefined): Block[] {
  if (!resp) return [];
  return resp.blocks.map((b) => ({
    weekday: b.weekday,
    startTime: b.startTime,
    endTime: b.endTime,
    tempId: makeTempId(),
  }));
}

function isDirty(local: Block[], original: WeeklyScheduleResponse | undefined): boolean {
  const a = local.map((b) => `${b.weekday}|${b.startTime}|${b.endTime}`).sort();
  const b = (original?.blocks ?? [])
    .map((x) => `${x.weekday}|${x.startTime}|${x.endTime}`)
    .sort();
  if (a.length !== b.length) return true;
  return a.some((x, i) => x !== b[i]);
}

type Props = {
  schedule: WeeklyScheduleResponse | undefined;
  readOnly: boolean;
  saving: boolean;
  saveError: unknown;
  onSave: (blocks: ScheduleBlock[]) => Promise<void>;
};

export function WeeklyScheduleEditor({
  schedule,
  readOnly,
  saving,
  saveError,
  onSave,
}: Props): ReactNode {
  const { t } = useTranslation();
  const [blocks, setBlocks] = useState<Block[]>(() => blocksFromResponse(schedule));

  // Reset stanu lokalnego, kiedy zmieni się pracownik (a więc i `schedule`).
  useEffect(() => {
    setBlocks(blocksFromResponse(schedule));
  }, [schedule]);

  const dirty = useMemo(() => isDirty(blocks, schedule), [blocks, schedule]);

  const byDay = useMemo(() => {
    const map = new Map<number, Block[]>();
    for (const b of blocks) {
      const arr = map.get(b.weekday) ?? [];
      arr.push(b);
      map.set(b.weekday, arr);
    }
    for (const arr of map.values()) {
      arr.sort((x, y) => x.startTime.localeCompare(y.startTime));
    }
    return map;
  }, [blocks]);

  const addBlock = (weekday: number) => {
    setBlocks((prev) => [
      ...prev,
      { weekday, startTime: '09:00', endTime: '17:00', tempId: makeTempId() },
    ]);
  };

  const updateBlock = (tempId: string, patch: Partial<ScheduleBlock>) => {
    setBlocks((prev) =>
      prev.map((b) => (b.tempId === tempId ? { ...b, ...patch } : b)),
    );
  };

  const removeBlock = (tempId: string) => {
    setBlocks((prev) => prev.filter((b) => b.tempId !== tempId));
  };

  const handleSave = async () => {
    const payload: ScheduleBlock[] = blocks.map(({ weekday, startTime, endTime }) => ({
      weekday,
      startTime,
      endTime,
    }));
    await onSave(payload);
  };

  const handleReset = () => {
    setBlocks(blocksFromResponse(schedule));
  };

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-zinc-200">
        <table className="min-w-full divide-y divide-zinc-200">
          <thead className="bg-zinc-50">
            <tr className="text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
              <th className="w-44 px-4 py-3">{t('schedules.weekly.day')}</th>
              <th className="px-4 py-3">{t('schedules.weekly.blocks')}</th>
              {!readOnly && <th className="w-24 px-4 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 text-sm">
            {WEEKDAYS.map((wd) => {
              const dayBlocks = byDay.get(wd) ?? [];
              return (
                <tr key={wd} className="align-top">
                  <td className="px-4 py-3 font-medium text-zinc-700">
                    {t(`schedules.weekday.${wd}`)}
                  </td>
                  <td className="px-4 py-3">
                    {dayBlocks.length === 0 ? (
                      <span className="text-xs text-zinc-400">
                        {t('schedules.weekly.dayOff')}
                      </span>
                    ) : (
                      <div className="space-y-2">
                        {dayBlocks.map((b) => (
                          <div key={b.tempId} className="flex flex-wrap items-center gap-2">
                            <input
                              type="time"
                              value={b.startTime}
                              onChange={(e) =>
                                updateBlock(b.tempId, { startTime: e.target.value })
                              }
                              disabled={readOnly}
                              className="input w-28"
                            />
                            <span className="text-zinc-400">–</span>
                            <input
                              type="time"
                              value={b.endTime}
                              onChange={(e) =>
                                updateBlock(b.tempId, { endTime: e.target.value })
                              }
                              disabled={readOnly}
                              className="input w-28"
                            />
                            {!readOnly && (
                              <button
                                type="button"
                                onClick={() => removeBlock(b.tempId)}
                                className="text-xs text-red-600 hover:underline"
                              >
                                {t('schedules.weekly.removeBlock')}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => addBlock(wd)}
                        className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-700 transition hover:bg-zinc-50"
                      >
                        + {t('schedules.weekly.addBlock')}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {saveError ? (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {extractApiError(saveError)}
        </div>
      ) : null}

      {!readOnly && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleReset}
            disabled={!dirty || saving}
          >
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  );
}
